import { useState } from 'react'
import { MessageSquare, Search, AlertCircle, Clock, CheckCircle, Loader2, X } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useBetaFeedback } from '@/lib/data/hooks/useBetaFeedback'
import type { BetaFeedback } from '@/lib/data'

interface BetaFeedbackViewProps {
  client: SupabaseClient | null
}

export function BetaFeedbackView({ client }: BetaFeedbackViewProps) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved'>('all')
  const [search, setSearch] = useState('')
  const [selectedFeedback, setSelectedFeedback] = useState<BetaFeedback | null>(null)

  const { feedback, stats, isLoading, error, updateStatus } = useBetaFeedback(client, {
    status: statusFilter,
    search,
  })

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <MessageSquare size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view beta feedback</p>
      </div>
    )
  }

  const handleStatusUpdate = async (newStatus: BetaFeedback['status']) => {
    if (!selectedFeedback) return
    const success = await updateStatus(selectedFeedback.id, newStatus)
    if (success) {
      setSelectedFeedback((prev: BetaFeedback | null) => (prev ? { ...prev, status: newStatus } : null))
    }
  }

  const severityColor = {
    critical: 'bg-red-500/10 text-red-400 border-red-500/30',
    high: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    low: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  }

  const statusColor = {
    open: 'bg-red-500/10 text-red-400',
    in_progress: 'bg-amber-500/10 text-amber-400',
    resolved: 'bg-emerald-500/10 text-emerald-400',
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-[var(--text-main)]">Beta Feedback</h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => setStatusFilter('open')}
          className={`bg-[var(--bg-card)] border p-4 rounded-xl text-left transition-colors ${
            statusFilter === 'open' ? 'border-red-400' : 'border-[var(--border)] hover:border-[var(--text-muted)]'
          }`}
        >
          <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm mb-1">
            <AlertCircle size={16} className="text-red-400" />
            Open
          </div>
          <p className="text-2xl font-bold text-red-400">{stats.open}</p>
        </button>
        <button
          onClick={() => setStatusFilter('in_progress')}
          className={`bg-[var(--bg-card)] border p-4 rounded-xl text-left transition-colors ${
            statusFilter === 'in_progress' ? 'border-amber-400' : 'border-[var(--border)] hover:border-[var(--text-muted)]'
          }`}
        >
          <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm mb-1">
            <Clock size={16} className="text-amber-400" />
            In Progress
          </div>
          <p className="text-2xl font-bold text-amber-400">{stats.inProgress}</p>
        </button>
        <button
          onClick={() => setStatusFilter('resolved')}
          className={`bg-[var(--bg-card)] border p-4 rounded-xl text-left transition-colors ${
            statusFilter === 'resolved' ? 'border-emerald-400' : 'border-[var(--border)] hover:border-[var(--text-muted)]'
          }`}
        >
          <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm mb-1">
            <CheckCircle size={16} className="text-emerald-400" />
            Resolved
          </div>
          <p className="text-2xl font-bold text-emerald-400">{stats.resolved}</p>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search feedback..."
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
          />
        </div>
        {statusFilter !== 'all' && (
          <button
            onClick={() => setStatusFilter('all')}
            className="px-3 py-2 bg-[var(--bg-element)] text-[var(--text-muted)] rounded-lg hover:bg-[var(--bg-hover)]"
          >
            Clear Filter
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Feedback List */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : feedback.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            No feedback found
          </div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {feedback.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedFeedback(item)}
                className="w-full p-4 text-left hover:bg-[var(--bg-element)] transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${statusColor[item.status]}`}>
                        {item.status.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${severityColor[item.severity]}`}>
                        {item.severity}
                      </span>
                      <span className="text-[var(--text-muted)] text-xs">{item.type}</span>
                    </div>
                    <p className="text-[var(--text-main)] font-medium">{item.title}</p>
                    <p className="text-[var(--text-muted)] text-sm line-clamp-2 mt-1">{item.description}</p>
                  </div>
                  <p className="text-[var(--text-muted)] text-xs">
                    {new Date(item.submitted_at).toLocaleDateString()}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedFeedback && (
        <FeedbackDetailModal
          feedback={selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          onStatusUpdate={handleStatusUpdate}
          severityColor={severityColor}
          statusColor={statusColor}
        />
      )}
    </div>
  )
}

interface FeedbackDetailModalProps {
  feedback: BetaFeedback
  onClose: () => void
  onStatusUpdate: (status: BetaFeedback['status']) => void
  severityColor: Record<string, string>
  statusColor: Record<string, string>
}

function FeedbackDetailModal({ feedback, onClose, onStatusUpdate, severityColor, statusColor }: FeedbackDetailModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="text-xl font-bold text-[var(--text-main)]">Feedback Details</h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-main)]">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Header Info */}
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 text-sm rounded-full ${statusColor[feedback.status]}`}>
              {feedback.status.replace('_', ' ')}
            </span>
            <span className={`px-3 py-1 text-sm rounded-full border ${severityColor[feedback.severity]}`}>
              {feedback.severity}
            </span>
            <span className="text-[var(--text-muted)]">{feedback.type}</span>
          </div>

          {/* Title */}
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-main)]">{feedback.title}</h3>
            <p className="text-[var(--text-muted)] text-sm mt-1">
              Submitted on {new Date(feedback.submitted_at).toLocaleString()}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-[var(--text-muted)] mb-2 block">Description</label>
            <p className="text-[var(--text-main)] bg-[var(--bg-element)] p-4 rounded-lg">
              {feedback.description}
            </p>
          </div>

          {/* Admin Notes */}
          {feedback.admin_notes && (
            <div>
              <label className="text-sm text-[var(--text-muted)] mb-2 block">Admin Notes</label>
              <p className="text-[var(--text-main)] bg-[var(--bg-element)] p-4 rounded-lg">
                {feedback.admin_notes}
              </p>
            </div>
          )}

          {/* Status Actions */}
          <div>
            <label className="text-sm text-[var(--text-muted)] mb-2 block">Update Status</label>
            <div className="flex gap-2">
              <button
                onClick={() => onStatusUpdate('open')}
                disabled={feedback.status === 'open'}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  feedback.status === 'open'
                    ? 'bg-red-500 text-white'
                    : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                }`}
              >
                Open
              </button>
              <button
                onClick={() => onStatusUpdate('in_progress')}
                disabled={feedback.status === 'in_progress'}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  feedback.status === 'in_progress'
                    ? 'bg-amber-500 text-white'
                    : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                }`}
              >
                In Progress
              </button>
              <button
                onClick={() => onStatusUpdate('resolved')}
                disabled={feedback.status === 'resolved'}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  feedback.status === 'resolved'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                }`}
              >
                Resolved
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
